import { useMutation, useQueryClient } from '@tanstack/react-query';
import { meDraftOffers, userPreferences } from '@ooh/contracts';
import type { LastUsedOfferDraftResponse, SetLastUsedOfferDraftBody } from '@ooh/contracts';
import { apiQueryKey } from '../../../data/use-api-suspense-query';
import { useApiQuery } from '../../../data/use-api-query';
import { useApiClient } from '../../../plugs/api-client';
import { useCurrentUser } from '../../../plugs/current-user';

export interface LastUsedOfferDraftQueryData {
  lastUsedOfferDraft: {
    offerId: string;
    name: string;
    totalUnitCount: number;
  } | null;
}

export function selectLastUsedOfferDraft(
  data: LastUsedOfferDraftResponse,
): LastUsedOfferDraftQueryData {
  return {
    lastUsedOfferDraft: data.lastUsedOfferDraft
      ? {
          offerId: String(data.lastUsedOfferDraft.offerId),
          name: data.lastUsedOfferDraft.name,
          totalUnitCount: data.lastUsedOfferDraft.totalUnitCount,
        }
      : null,
  };
}

/** `enabled: false` skips the fetch, e.g. when the destination offer is already pinned. */
export function useLastUsedOfferDraftQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useCurrentUser();
  const { data, isLoading } = useApiQuery(
    userPreferences.getLastUsedOfferDraft,
    {},
    {
      enabled,
      queryKey: [...apiQueryKey(userPreferences.getLastUsedOfferDraft, {}), user?.id ?? null],
      select: selectLastUsedOfferDraft,
    },
  );
  return { data, isLoading };
}

export function useSetLastUsedOfferDraftMutation() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SetLastUsedOfferDraftBody) =>
      api.call(userPreferences.setLastUsedOfferDraft, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: apiQueryKey(meDraftOffers.list) }),
        queryClient.invalidateQueries({
          queryKey: apiQueryKey(userPreferences.getLastUsedOfferDraft),
        }),
      ]);
    },
  });
}

/**
 * Shared by `useQuickAddPipeline.confirm` and `useCalendarOfferPicker.handleCalendarPickerConfirm`
 * (CU-86cb8grgq): marks a quick-add-confirmed offer as the user's last-used draft, which
 * invalidates both the editable-drafts list and the last-used-draft query so the picker's
 * compact trigger and reopened list pick up the offer immediately.
 *
 * Locked-target mode (`isLockedTarget: true`) pins the destination from URL/offer context
 * rather than the user picking a draft — "remember the last draft I chose" doesn't apply,
 * and for non-DRAFT offers the endpoint rejects this write with a 403 anyway.
 */
export function markOfferAsLastUsedDraft(
  offerId: string,
  isLockedTarget: boolean | undefined,
  setLastUsedOfferDraft: ReturnType<typeof useSetLastUsedOfferDraftMutation>,
): void {
  if (isLockedTarget) return;
  setLastUsedOfferDraft.mutate({
    offerId: Number(offerId) as SetLastUsedOfferDraftBody['offerId'],
  });
}
